import { postEventImages2025 } from 'constants/images'
import React from 'react'

const Run2025: React.FC = () => {
    interface EventImageProps {
        src: string
        imageText?: string
    }

    const EventImage: React.FC<EventImageProps> = ({ src, imageText }) => {
        return (
            <img
                src={src}
                loading="lazy"
                alt={`Running event ${imageText}`}
                className="w-full rounded-2xl shadow-md hover:opacity-90 transition"
            />
        )
    }

    return (
        <div>
            <section
                className="relative bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(assets/Kilverts-Field.jpg)` }}>
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-white/25 to-white/25 bg-white/25"></div>

                {/* Top Wave Background - Mobile */}
                <div className="block lg:hidden">
                    <div className="absolute top-0 left-0 w-full overflow-hidden leading-[0] z-10">
                        <svg
                            className="block w-full h-[400px]"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="600 0 500 350" /* min-x min-y width height */
                            preserveAspectRatio="none">
                            <path
                                d="M0,0V80c70,30,150,50,230,30s150-90,230-90,160,60,240,80,160-20,240-30,160,40,240,30V0Z"
                                fill="#6aa4eb"
                            />
                        </svg>
                    </div>
                    <div className="absolute top-0 left-0 w-full overflow-hidden leading-[0] z-10">
                        <svg
                            className="block w-full h-[400px]"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="600 0 500 400" /* min-x min-y width height */
                            preserveAspectRatio="none">
                            <path
                                d="M0,0V80c70,30,150,50,230,30s150-90,230-90,160,60,240,80,160-20,240-30,160,40,240,30V0Z"
                                fill="#0071c0"
                            />
                        </svg>
                    </div>
                </div>

                {/* Top Wave Background - Desktop */}
                <div className="hidden lg:block">
                    <div className="absolute top-0 left-0 w-full overflow-hidden leading-[0] z-10">
                        <svg
                            className="block w-full h-[400px]"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="600 0 500 200" /* min-x min-y width height */
                            preserveAspectRatio="none">
                            <path
                                d="M0,0V80c70,30,150,50,230,30s150-90,230-90,160,60,240,80,160-20,240-30,160,40,240,30V0Z"
                                fill="#69b4eb"
                            />
                        </svg>
                    </div>
                    <div className="absolute top-0 left-0 w-full overflow-hidden leading-[0] z-10">
                        <svg
                            className="block w-full h-[400px]"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="600 0 500 250" /* min-x min-y width height */
                            preserveAspectRatio="none">
                            <path
                                d="M0,0V80c70,30,150,50,230,30s150-90,230-90,160,60,240,80,160-20,240-30,160,40,240,30V0Z"
                                fill="#0071c0"
                            />
                        </svg>
                    </div>
                </div>

                {/* Header - Text */}
                <div className="absolute ml-4 mt-2 z-10">
                    <h1 className="text-3xl text-white font-extrabold sm:text-5xl">
                        Grays Run
                    </h1>
                    <h2 className="text-1xl text-yellow-400 font-extrabold sm:text-3xl">
                        Walk, Jog or Run
                    </h2>
                </div>

                {/* Content */}
                <div className="relative flex flex-col justify-end items-center lg:justify-center lg:items-center text-center mx-auto max-w-screen-xl px-4 pt-32 pb-16 sm:px-6 lg:h-screen min-h-[500px] z-10">
                    <div className="max-w-xl lg:max-w-5xl">
                        <h1 className="text-3xl font-extrabold sm:text-5xl">
                            Grays Run 7th September 2025
                        </h1>
                    </div>
                    <div className="max-w-xl lg:max-w-3xl">
                        <p className="mt-4 sm:text-xl/relaxed font-bold">
                            <a
                                href="https://www.racetimeresult.co.uk/results/grays-riverside-10k"
                                target="_blank"
                                rel="noreferrer"
                                className="text-white grays-run-bg-blue hover:bg-primary-800 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center my-4">
                                Race results
                            </a>
                        </p>
                    </div>
                </div>
            </section>

            {/* Masonry-style grid */}
            <div className="m-8">
                {/* <div className="columns-1 sm:columns-2 md:columns-3 gap-4 space-y-4"> */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    <EventImage src={postEventImages2025.start1} imageText="Start 1" />
                    <EventImage
                        src={postEventImages2025.theVolunteers}
                        imageText="The Volunteers"
                    />
                    <EventImage
                        src={postEventImages2025.djReadyToRock}
                        imageText="DJ Ready To Rock"
                    />
                    <EventImage
                        src={postEventImages2025.startTheWarmUp}
                        imageText="Start The Warm Up"
                    />
                    <EventImage src={postEventImages2025.warmUp} imageText="Warm Up" />
                    <EventImage
                        src={postEventImages2025.howLowCanYouGo}
                        imageText="How Low Can You Go"
                    />
                    <EventImage
                        src={postEventImages2025.requestedPhoto}
                        imageText="Requested Photo"
                    />
                    <EventImage src={postEventImages2025.onTheWay} imageText="On The Way" />
                    <EventImage src={postEventImages2025.prizes} imageText="Prizes" />
                    <EventImage src={postEventImages2025.firstLady} imageText="1st Lady" />
                    <EventImage src={postEventImages2025.secondLady} imageText="2nd Lady" />
                    <EventImage
                        src={postEventImages2025.dogAcrossLine}
                        imageText="Dog Across Line"
                    />
                    <EventImage
                        src={postEventImages2025.raceForTheLine}
                        imageText="Race For The Line"
                    />
                    <EventImage src={postEventImages2025.powerWalker} imageText="Power Walker" />
                    <EventImage
                        src={postEventImages2025.menRaceWinners}
                        imageText="Men Race Winners"
                    />
                    <EventImage src={postEventImages2025.womenWinners} imageText="Women Winners" />
                    <EventImage src={postEventImages2025.readyFor3k} imageText="Ready For The 3K" />
                    <EventImage src={postEventImages2025.letsGo3k} imageText="Let's Go 3K" />
                    <EventImage src={postEventImages2025.secondPlace} imageText="2nd Place" />
                    <EventImage
                        src={postEventImages2025.thirdPlaceGirl}
                        imageText="3rd Place 1st Girl"
                    />
                    <EventImage src={postEventImages2025.runningWell} imageText="Running Well" />
                    <EventImage
                        src={postEventImages2025.runningTogether}
                        imageText="Running Together"
                    />
                    <EventImage
                        src={postEventImages2025.joyOfRunning}
                        imageText="The Joy Of Running"
                    />
                    <EventImage
                        src={postEventImages2025.fourMinutesBehind}
                        imageText="4 Minutes Behind"
                    />
                    <EventImage src={postEventImages2025.thirtyFive} imageText="35" />
                    <EventImage
                        src={postEventImages2025.thirtyEightAndFortySeven}
                        imageText="38 and 47"
                    />
                    <EventImage src={postEventImages2025.oneThreeThree} imageText="133" />
                    <EventImage
                        src={postEventImages2025.charityRunners}
                        imageText="Charity Runners"
                    />
                    <EventImage
                        src={postEventImages2025.eventualWinner}
                        imageText="Eventual Winner"
                    />
                    <EventImage src={postEventImages2025.img2906} imageText="IMG 2906" />
                    <EventImage src={postEventImages2025.img2907} imageText="IMG 2907" />
                    <EventImage src={postEventImages2025.img2914} imageText="IMG 2914" />
                    <EventImage src={postEventImages2025.img2915} imageText="IMG 2915" />
                    <EventImage src={postEventImages2025.img2916} imageText="IMG 2916" />
                    <EventImage src={postEventImages2025.img2917} imageText="IMG 2917" />
                    <EventImage src={postEventImages2025.img2918} imageText="IMG 2918" />
                    <EventImage src={postEventImages2025.img2920} imageText="IMG 2920" />
                    <EventImage src={postEventImages2025.img2921} imageText="IMG 2921" />
                    <EventImage src={postEventImages2025.img2922} imageText="IMG 2922" />
                    <EventImage src={postEventImages2025.img2924} imageText="IMG 2924" />
                    <EventImage src={postEventImages2025.img2926} imageText="IMG 2926" />
                    <EventImage src={postEventImages2025.img2927} imageText="IMG 2927" />
                    <EventImage src={postEventImages2025.img2928} imageText="IMG 2928" />
                    <EventImage src={postEventImages2025.img2929} imageText="IMG 2929" />
                    <EventImage src={postEventImages2025.img2930} imageText="IMG 2930" />
                    <EventImage src={postEventImages2025.img2931} imageText="IMG 2931" />
                    <EventImage src={postEventImages2025.img2932} imageText="IMG 2932" />
                    <EventImage src={postEventImages2025.img2934} imageText="IMG 2934" />
                    <EventImage src={postEventImages2025.img2935} imageText="IMG 2935" />
                    <EventImage src={postEventImages2025.img2937} imageText="IMG 2937" />
                    <EventImage src={postEventImages2025.img2938} imageText="IMG 2938" />
                    <EventImage src={postEventImages2025.img2940} imageText="IMG 2940" />
                    <EventImage src={postEventImages2025.img2944} imageText="IMG 2944" />
                    <EventImage src={postEventImages2025.img2945} imageText="IMG 2945" />
                    <EventImage src={postEventImages2025.img2946} imageText="IMG 2946" />
                    <EventImage src={postEventImages2025.img2948} imageText="IMG 2948" />
                    <EventImage src={postEventImages2025.img2949} imageText="IMG 2949" />
                    <EventImage src={postEventImages2025.img2950} imageText="IMG 2950" />
                    <EventImage src={postEventImages2025.img2951} imageText="IMG 2951" />
                    <EventImage src={postEventImages2025.img2953} imageText="IMG 2953" />
                    <EventImage src={postEventImages2025.img2954} imageText="IMG 2954" />
                    <EventImage src={postEventImages2025.img2955} imageText="IMG 2955" />
                    <EventImage src={postEventImages2025.img2956} imageText="IMG 2956" />
                    <EventImage src={postEventImages2025.img2957} imageText="IMG 2957" />
                    <EventImage src={postEventImages2025.img2959} imageText="IMG 2959" />
                    <EventImage src={postEventImages2025.img2960} imageText="IMG 2960" />
                    <EventImage src={postEventImages2025.img2961} imageText="IMG 2961" />
                    <EventImage src={postEventImages2025.img2962} imageText="IMG 2962" />
                    <EventImage src={postEventImages2025.img2964} imageText="IMG 2964" />
                    <EventImage src={postEventImages2025.img2965} imageText="IMG 2965" />
                    <EventImage src={postEventImages2025.img2967} imageText="IMG 2967" />
                    <EventImage src={postEventImages2025.img2968} imageText="IMG 2968" />
                    <EventImage src={postEventImages2025.img2971} imageText="IMG 2971" />
                    <EventImage src={postEventImages2025.img2973} imageText="IMG 2973" />
                    <EventImage src={postEventImages2025.img2974} imageText="IMG 2974" />
                    <EventImage src={postEventImages2025.img2975} imageText="IMG 2975" />
                    <EventImage src={postEventImages2025.img2976} imageText="IMG 2976" />
                    <EventImage src={postEventImages2025.img2977} imageText="IMG 2977" />
                    <EventImage src={postEventImages2025.img2978} imageText="IMG 2978" />
                    <EventImage src={postEventImages2025.img2979} imageText="IMG 2979" />
                    <EventImage src={postEventImages2025.img2980} imageText="IMG 2980" />
                    <EventImage src={postEventImages2025.img2981} imageText="IMG 2981" />
                    <EventImage src={postEventImages2025.img2982} imageText="IMG 2982" />
                    <EventImage src={postEventImages2025.img2983} imageText="IMG 2983" />
                    <EventImage src={postEventImages2025.img2985} imageText="IMG 2985" />
                    <EventImage src={postEventImages2025.img2986} imageText="IMG 2986" />
                    <EventImage src={postEventImages2025.img2988} imageText="IMG 2988" />
                    <EventImage src={postEventImages2025.img2989} imageText="IMG 2989" />
                    <EventImage src={postEventImages2025.img2990} imageText="IMG 2990" />
                    <EventImage src={postEventImages2025.img2991} imageText="IMG 2991" />
                    <EventImage src={postEventImages2025.img2994} imageText="IMG 2994" />
                    <EventImage src={postEventImages2025.img2996} imageText="IMG 2996" />
                    <EventImage src={postEventImages2025.img2997} imageText="IMG 2997" />
                    <EventImage src={postEventImages2025.img2998} imageText="IMG 2998" />
                    <EventImage src={postEventImages2025.img3001} imageText="IMG 3001" />
                    <EventImage src={postEventImages2025.img3002} imageText="IMG 3002" />
                    <EventImage src={postEventImages2025.img3003} imageText="IMG 3003" />
                    <EventImage src={postEventImages2025.img3004} imageText="IMG 3004" />
                    <EventImage src={postEventImages2025.img3006} imageText="IMG 3006" />
                    <EventImage src={postEventImages2025.img3007} imageText="IMG 3007" />
                    <EventImage src={postEventImages2025.img3008} imageText="IMG 3008" />
                    <EventImage src={postEventImages2025.img3009} imageText="IMG 3009" />
                    <EventImage src={postEventImages2025.img3010} imageText="IMG 3010" />
                    <EventImage src={postEventImages2025.img3012} imageText="IMG 3012" />
                    <EventImage src={postEventImages2025.img3013} imageText="IMG 3013" />
                    <EventImage src={postEventImages2025.img3014} imageText="IMG 3014" />
                    <EventImage src={postEventImages2025.img3015} imageText="IMG 3015" />
                    <EventImage src={postEventImages2025.img3016} imageText="IMG 3016" />
                    <EventImage src={postEventImages2025.img3018} imageText="IMG 3018" />
                    <EventImage src={postEventImages2025.img3019} imageText="IMG 3019" />
                    <EventImage src={postEventImages2025.img3020} imageText="IMG 3020" />
                    <EventImage src={postEventImages2025.img3021} imageText="IMG 3021" />
                    <EventImage src={postEventImages2025.img3022} imageText="IMG 3022" />
                    <EventImage src={postEventImages2025.img3023} imageText="IMG 3023" />
                    <EventImage src={postEventImages2025.img3025} imageText="IMG 3025" />
                    <EventImage src={postEventImages2025.img3029} imageText="IMG 3029" />
                    <EventImage src={postEventImages2025.img3030} imageText="IMG 3030" />
                    <EventImage src={postEventImages2025.img3031} imageText="IMG 3031" />
                    <EventImage src={postEventImages2025.img3032} imageText="IMG 3032" />
                    <EventImage src={postEventImages2025.img3035} imageText="IMG 3035" />
                </div>
            </div>
        </div>
    )
}

export default Run2025
